import type { ChangeEvent } from "react";
import { type SxProps, type Theme } from "@mui/material/styles";

import { Chip } from "ui/chip";
import { Box } from "ui/box";
import { Button } from "ui/button";
import { Panel } from "ui/panel";
import { TextArea } from "ui/text-area";
import { FieldLabel } from "ui/forms";

import { shadows, zIndex } from "design-tokens";

import { AI_CONTENT_PREVIEW_AREA_OPTIONS } from "./ai-content-feedback.constants";
import type { AiContentFeedbackAction, AiContentPreviewArea } from "./ai-content-feedback.types";

type AiContentFeedbackPanelProps = {
  selectedAreas: AiContentPreviewArea[];
  feedback: string;
  isSubmitDisabled: boolean;
  onAreaToggle: (area: AiContentPreviewArea) => void;
  onFeedbackChange: (value: string) => void;
  onClose: () => void;
  onCancel: () => void;
  onSubmit: (action: AiContentFeedbackAction) => void;
};

const PANEL_WIDTH_PX = 448;

/**
 * Pinned to the viewport's corner rather than anchored to a trigger, so it paints its own surface. The
 * general comments FAB is pinned to the same corner at `zIndex.modal`, so the panel needs the next step
 * up to cover it instead of opening underneath it. `shadows.lg` is the floating-panel depth, which the
 * theme's elevation ladder deliberately leaves out.
 */
const FLOATING_PANEL_SX: SxProps<Theme> = (theme) => ({
  position: "fixed",
  bottom: theme.spacing(2),
  right: theme.spacing(2),
  zIndex: zIndex.popover,
  maxWidth: `calc(100vw - ${theme.spacing(4)})`,
  overflow: "hidden",
  borderRadius: `${theme.shape.large}px`,
  border: `1px solid ${theme.palette.border.secondary}`,
  backgroundColor: theme.palette.background.paper,
  boxShadow: shadows.lg,
});

const CONTENT_SX: SxProps<Theme> = { display: "flex", flexDirection: "column", gap: 3 };

const AiContentFeedbackPanel = ({
  selectedAreas,
  feedback,
  isSubmitDisabled,
  onAreaToggle,
  onFeedbackChange,
  onClose,
  onCancel,
  onSubmit,
}: AiContentFeedbackPanelProps) => {
  function handleFeedbackChange(event: ChangeEvent<HTMLTextAreaElement>) {
    onFeedbackChange(event.target.value);
  }

  function handleSendFeedbackClick() {
    onSubmit("send_feedback");
  }

  function handleRewriteClick() {
    onSubmit("rewrite");
  }

  return (
    <Panel
      title="Share your feedback"
      subtitle="Your feedback is strictly internal and used only to improve generation quality."
      onClose={onClose}
      closeLabel="Close feedback panel"
      width={PANEL_WIDTH_PX}
      role="dialog"
      data-testid="ai-content-feedback-panel"
      sx={FLOATING_PANEL_SX}>
      <Box sx={CONTENT_SX}>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <p className="text-sm font-bold leading-5 text-gray-900">Preview area</p>
            <div className="flex flex-wrap gap-1.5">
              {AI_CONTENT_PREVIEW_AREA_OPTIONS.map((option) => {
                const isSelected = selectedAreas.includes(option.id);

                return (
                  <Chip
                    aria-pressed={isSelected}
                    color="primary"
                    key={option.id}
                    label={option.label}
                    onClick={() => onAreaToggle(option.id)}
                    size="small"
                    variant={isSelected ? "filled" : "outlined"}
                  />
                );
              })}
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <FieldLabel
              name="ai-content-feedback"
              required
              sx={(theme) => ({
                minHeight: "1.25rem",
                fontSize: theme.typography.body2.fontSize,
                lineHeight: "1.25rem",
              })}>
              Feedback
            </FieldLabel>
            <TextArea
              name="ai-content-feedback"
              value={feedback}
              onChange={handleFeedbackChange}
              rows={6}
            />
          </div>
        </div>

        <div className="flex items-center justify-between gap-3">
          <Button variant="text" size="medium" onClick={onCancel}>
            Cancel
          </Button>
          <div className="flex items-center gap-3">
            <Button
              variant="outlined"
              size="medium"
              disabled={isSubmitDisabled}
              onClick={handleSendFeedbackClick}>
              Send feedback
            </Button>
            <Button
              variant="contained"
              size="medium"
              disabled={isSubmitDisabled}
              onClick={handleRewriteClick}>
              Rewrite
            </Button>
          </div>
        </div>
      </Box>
    </Panel>
  );
};

export default AiContentFeedbackPanel;
